import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Upload, Plus, X, Navigation, Loader2, ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateTourPage = () => {
  const navigate = useNavigate();
  const [gettingLocation, setGettingLocation] = useState(false);
  const [tourData, setTourData] = useState({
    title: '',
    city: '',
    description: '',
    duration: '',
    base_price: '',
    group_addon_price: '',
    allow_additional_devices: true,
    preview_audio_url: '',
    cover_image_url: '',
    tour_type: 'walking',
    // Private Access (invitation-only / VIP / pitch / pilot)
    is_private: false,
    access_code: '',
    is_unlisted: false,
    is_free: false,
  });
  const [stops, setStops] = useState([]);
  const [currentStop, setCurrentStop] = useState({
    stop_number: 1,
    title: '',
    latitude: '',
    longitude: '',
    radius_meters: 50,
    audio_url: '',
    audio_type: 'external',
    is_intro: false,
    navigation_mode: 'walking',
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingPreviewAudio, setUploadingPreviewAudio] = useState(false);
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false);
  const [saving, setSaving] = useState(false);

  // Handle preview audio upload
  const handlePreviewAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingPreviewAudio(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/audio/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTourData(prev => ({
        ...prev,
        preview_audio_url: `${BACKEND_URL}${response.data.audio_url}`,
      }));
      toast.success('Preview audio uploaded');
    } catch (error) {
      toast.error('Failed to upload preview audio');
    } finally {
      setUploadingPreviewAudio(false);
    }
  };

  // Handle cover image upload
  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingCoverImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/image/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTourData(prev => ({
        ...prev,
        cover_image_url: `${BACKEND_URL}${response.data.image_url}`,
      }));
      toast.success('Cover image uploaded');
    } catch (error) {
      toast.error('Failed to upload cover image');
    } finally {
      setUploadingCoverImage(false);
    }
  };

  // Calculate distance between two GPS coordinates in meters
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    const R = 6371000; // Earth's radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
  };

  // Find nearest stop and suggest max radius to avoid overlap
  const getRadiusSuggestion = (currentLat, currentLon) => {
    if (!currentLat || !currentLon) return { nearestDistance: null, suggestedMax: null, nearestStop: null };
    
    let nearestDistance = Infinity;
    let nearestStop = null;
    
    for (const stop of stops) {
      // Skip intro stops (they don't have GPS)
      if (stop.is_intro || !stop.latitude || !stop.longitude) continue;
      
      const distance = calculateDistance(currentLat, currentLon, stop.latitude, stop.longitude);
      if (distance && distance < nearestDistance) {
        nearestDistance = distance;
        nearestStop = stop;
      }
    }
    
    if (nearestDistance === Infinity) {
      return { nearestDistance: null, suggestedMax: null, nearestStop: null };
    }
    
    const suggestedMax = Math.floor(nearestDistance / 2);
    return { nearestDistance, suggestedMax, nearestStop };
  };

  // Check if radius overlaps with nearby stops
  const checkRadiusOverlap = (lat, lon, radius) => {
    const overlaps = [];
    
    for (const stop of stops) {
      if (stop.is_intro || !stop.latitude || !stop.longitude) continue;
      
      const distance = calculateDistance(lat, lon, stop.latitude, stop.longitude);
      if (!distance) continue;
      
      const otherRadius = stop.radius_meters || 50;
      if (distance < radius + otherRadius) {
        overlaps.push({
          stop,
          distance,
          overlapAmount: (radius + otherRadius) - distance
        });
      }
    }
    
    return overlaps;
  };

  const handleTourInputChange = (e) => {
    const { name, value } = e.target;
    setTourData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStopInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentStop((prev) => ({ ...prev, [name]: value }));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentStop(prev => ({
          ...prev,
          latitude: latitude.toFixed(6),
          longitude: longitude.toFixed(6),
        }));
        toast.success(`Location captured: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
        setGettingLocation(false);
      },
      (error) => {
        let message = 'Failed to get location';
        if (error.code === 1) message = 'Location access denied. Please enable location permissions.';
        else if (error.code === 2) message = 'Location unavailable. Please try again.';
        else if (error.code === 3) message = 'Location request timed out. Please try again.';
        toast.error(message);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const moveStop = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= stops.length) return;

    const newStops = [...stops];
    [newStops[index], newStops[newIndex]] = [newStops[newIndex], newStops[index]];
    
    // Update stop numbers
    newStops.forEach((stop, i) => {
      stop.stop_number = i + 1;
    });
    
    setStops(newStops);
    toast.success('Stop order updated');
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/audio/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCurrentStop((prev) => ({
        ...prev,
        audio_url: `${BACKEND_URL}${response.data.audio_url}`,
        audio_type: 'uploaded',
      }));
      toast.success('Audio uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload audio');
    } finally {
      setUploading(false);
    }
  };

  const addStop = () => {
    if (!currentStop.title) {
      toast.error('Please fill in the stop title');
      return;
    }
    // For intro stops, GPS is optional. For regular stops, GPS is required.
    if (!currentStop.is_intro && (!currentStop.latitude || !currentStop.longitude)) {
      toast.error('Please fill in GPS coordinates (or mark as Intro to skip)');
      return;
    }

    const stopToAdd = {
      ...currentStop,
      stop_number: stops.length + 1,
      latitude: currentStop.latitude ? parseFloat(currentStop.latitude) : null,
      longitude: currentStop.longitude ? parseFloat(currentStop.longitude) : null,
      radius_meters: parseFloat(currentStop.radius_meters) || 50,
    };

    setStops((prev) => [...prev, stopToAdd]);
    setCurrentStop({
      stop_number: stops.length + 2,
      title: '',
      latitude: '',
      longitude: '',
      radius_meters: 50,
      audio_url: '',
      audio_type: 'external',
      is_intro: false,
      navigation_mode: 'walking',
    });
    toast.success('Stop added');
  };

  const removeStop = (index) => {
    setStops((prev) => prev.filter((_, i) => i !== index));
  };

  const saveTour = async (publishNow = false) => {
    if (!tourData.title || !tourData.city) {
      toast.error('Please fill in tour title and city');
      return;
    }

    // Only require stops if publishing
    if (publishNow && stops.length === 0) {
      toast.error('Please add at least one stop to publish');
      return;
    }

    setSaving(true);
    try {
      const tourPayload = {
        title: tourData.title,
        city: tourData.city,
        description: tourData.description || '',
        duration: tourData.duration || '',
        base_price: parseFloat(tourData.base_price) || 0,
        group_addon_price: parseFloat(tourData.group_addon_price) || 0,
        allow_additional_devices: tourData.allow_additional_devices,
        preview_audio_url: tourData.preview_audio_url || null,
        cover_image_url: tourData.cover_image_url || null,
        tour_type: tourData.tour_type || 'walking',
        is_private: !!tourData.is_private,
        access_code: tourData.is_private ? (tourData.access_code || '').trim() : '',
        is_unlisted: !!tourData.is_unlisted,
        is_free: !!tourData.is_free,
        published: publishNow,
      };
      
      console.log('Creating tour with payload:', tourPayload);
      const tourResponse = await axios.post(`${API}/admin/tours`, tourPayload);

      const tourId = tourResponse.data.tour_id;

      for (const stop of stops) {
        await axios.post(`${API}/admin/tours/${tourId}/stops`, {
          ...stop,
          latitude: parseFloat(stop.latitude) || 0,
          longitude: parseFloat(stop.longitude) || 0,
          radius_meters: parseFloat(stop.radius_meters) || 50,
        });
      }

      toast.success(publishNow ? 'Tour published successfully!' : 'Tour saved as draft!');
      navigate('/admin');
    } catch (error) {
      console.error('Failed to save tour:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to save tour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            data-testid="back-to-admin-button"
            className="flex items-center text-teal-400 hover:text-teal-300 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-4xl font-bold font-sans">Create New Tour</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 font-sans">Tour Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Tour Title</label>
              <input
                type="text"
                name="title"
                value={tourData.title}
                onChange={handleTourInputChange}
                data-testid="tour-title-input"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                placeholder="e.g., Historic Downtown Walk"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">City</label>
              <input
                type="text"
                name="city"
                value={tourData.city}
                onChange={handleTourInputChange}
                data-testid="tour-city-input"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                placeholder="e.g., New York"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Description</label>
              <textarea
                name="description"
                value={tourData.description}
                onChange={handleTourInputChange}
                data-testid="tour-description-input"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                rows="3"
                placeholder="Describe your tour..."
              ></textarea>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Duration</label>
                <input
                  type="text"
                  name="duration"
                  value={tourData.duration}
                  onChange={handleTourInputChange}
                  data-testid="tour-duration-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                  placeholder="e.g., 2 hours"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Base Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="base_price"
                  value={tourData.base_price}
                  onChange={handleTourInputChange}
                  data-testid="tour-base-price-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                  placeholder="15.00"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Group Add-on ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="group_addon_price"
                  value={tourData.group_addon_price}
                  onChange={handleTourInputChange}
                  data-testid="tour-group-price-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                  placeholder="5.00"
                  disabled={!tourData.allow_additional_devices}
                />
                
                {/* Toggle for Additional Devices */}
                <div className="mt-3 flex items-center gap-3 p-3 bg-slate-100 rounded-lg">
                  <input
                    type="checkbox"
                    id="allow-additional-devices"
                    checked={tourData.allow_additional_devices}
                    onChange={(e) => setTourData({ ...tourData, allow_additional_devices: e.target.checked })}
                    className="w-5 h-5 text-teal-600 rounded"
                    data-testid="allow-devices-checkbox"
                  />
                  <label htmlFor="allow-additional-devices" className="text-sm font-semibold text-slate-700">
                    Offer additional devices for this tour
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Uncheck to hide the "Add More Devices" option from customers
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Access & Pricing — private (invitation-only), unlisted, free */}
        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-2 font-sans">Access &amp; Pricing</h2>
          <p className="text-sm text-slate-600 mb-5 font-body">
            For invitation-only tours, pilots, VIP previews, museum demonstrations and live pitches.
          </p>

          {/* Free / Paid toggle */}
          <div className="mb-4 flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <input
              type="checkbox"
              id="tour-is-free"
              checked={tourData.is_free}
              onChange={(e) => setTourData({ ...tourData, is_free: e.target.checked })}
              className="w-5 h-5 text-emerald-600 rounded"
              data-testid="tour-is-free"
            />
            <label htmlFor="tour-is-free" className="flex-1">
              <div className="font-bold text-emerald-900">Free tour</div>
              <div className="text-xs text-emerald-700">No payment screen — users go straight into the tour after entering any required access code.</div>
            </label>
          </div>

          {/* Private Access toggle */}
          <div className="mb-4 flex items-center gap-3 p-4 bg-slate-900 text-white rounded-xl">
            <input
              type="checkbox"
              id="tour-is-private"
              checked={tourData.is_private}
              onChange={(e) => setTourData({ ...tourData, is_private: e.target.checked })}
              className="w-5 h-5 text-blue-500 rounded"
              data-testid="tour-is-private"
            />
            <label htmlFor="tour-is-private" className="flex-1">
              <div className="font-bold">Private Access — Access Code Required</div>
              <div className="text-xs text-slate-300">Users must enter a code on a dedicated screen before they can preview or start this tour.</div>
            </label>
          </div>

          {/* Access code input — only when private is on */}
          {tourData.is_private && (
            <div className="mb-4 pl-4 border-l-2 border-blue-500" data-testid="access-code-section">
              <label className="block text-sm font-bold text-slate-800 mb-2 uppercase tracking-wider">
                Access code
              </label>
              <input
                type="text"
                value={tourData.access_code}
                onChange={(e) => setTourData({ ...tourData, access_code: e.target.value })}
                placeholder="e.g. AUDIOSTRIDE2026"
                data-testid="tour-access-code"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-mono uppercase tracking-wider"
                autoComplete="off"
              />
              <p className="text-xs text-slate-500 mt-2">
                Stakeholders enter this code on the tour's landing page. You can change it anytime — old codes will stop working immediately.
              </p>
            </div>
          )}

          {/* Unlisted toggle */}
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <input
              type="checkbox"
              id="tour-is-unlisted"
              checked={tourData.is_unlisted}
              onChange={(e) => setTourData({ ...tourData, is_unlisted: e.target.checked })}
              className="w-5 h-5 text-amber-600 rounded"
              data-testid="tour-is-unlisted"
            />
            <label htmlFor="tour-is-unlisted" className="flex-1">
              <div className="font-bold text-amber-900">Unlisted — hide from public discovery</div>
              <div className="text-xs text-amber-700">Tour is still reachable via direct link, but won't appear in the homepage tour list. Useful for true invitation-only experiences.</div>
            </label>
          </div>
        </div>

        {/* Preview Audio & Cover Image Section */}
        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 font-sans">Media (Optional)</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Preview Audio */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <h3 className="font-bold text-purple-900 mb-3">🎵 Preview Audio</h3>
              <p className="text-sm text-purple-700 mb-3">
                A short audio clip that plays on the tour page before purchase.
              </p>
              
              {tourData.preview_audio_url ? (
                <div className="space-y-2">
                  <audio controls className="w-full">
                    <source src={tourData.preview_audio_url} type="audio/mpeg" />
                  </audio>
                  <button
                    type="button"
                    onClick={() => setTourData(prev => ({ ...prev, preview_audio_url: '' }))}
                    className="text-red-500 hover:text-red-700 text-sm font-bold"
                  >
                    Remove Preview Audio
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm font-bold">
                  {uploadingPreviewAudio ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload Preview Audio
                  <input
                    type="file"
                    accept="audio/*,.mp3,.wav,.m4a"
                    onChange={handlePreviewAudioUpload}
                    className="hidden"
                    disabled={uploadingPreviewAudio}
                  />
                </label>
              )}
            </div>
            
            {/* Cover Image */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-bold text-blue-900 mb-3">🖼️ Cover Image</h3>
              <p className="text-sm text-blue-700 mb-3">
                The main image displayed on the tour card and detail page.
              </p>
              
              {tourData.cover_image_url ? (
                <div className="space-y-2">
                  <img 
                    src={tourData.cover_image_url} 
                    alt="Cover" 
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setTourData(prev => ({ ...prev, cover_image_url: '' }))}
                    className="text-red-500 hover:text-red-700 text-sm font-bold"
                  >
                    Remove Cover Image
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm font-bold">
                  {uploadingCoverImage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload Cover Image
                  <input
                    type="file"
                    accept="image/*,.jpg,.jpeg,.png,.webp"
                    onChange={handleCoverImageUpload}
                    className="hidden"
                    disabled={uploadingCoverImage}
                  />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 font-sans">Add Stops</h2>
          
          {/* Quick Guide */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6">
            <h4 className="font-bold text-blue-900 mb-2">Quick Guide: Stop Types</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li><span className="font-semibold">🎙️ Intro Stop:</span> Plays from anywhere - no GPS needed. Perfect for tour introductions.</li>
              <li><span className="font-semibold">🚶 Walking:</span> Users walk to this location. Audio plays when they enter the GPS radius.</li>
              <li><span className="font-semibold">🚗 Driving:</span> Users drive to this location. Great for stops far apart.</li>
            </ul>
          </div>
          
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Stop Title *</label>
              <input
                type="text"
                name="title"
                value={currentStop.title}
                onChange={handleStopInputChange}
                data-testid="stop-title-input"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                placeholder="e.g., Central Park"
              />
            </div>
            
            {/* Intro Stop Toggle */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is-intro-checkbox"
                  checked={currentStop.is_intro || false}
                  onChange={(e) => setCurrentStop(prev => ({ ...prev, is_intro: e.target.checked }))}
                  className="w-5 h-5 text-teal-600 rounded"
                  data-testid="stop-is-intro-checkbox"
                />
                <label htmlFor="is-intro-checkbox" className="text-sm font-semibold text-teal-800">
                  This is an Intro Stop (plays from anywhere - no GPS needed)
                </label>
              </div>
              <p className="text-xs text-teal-600 mt-2 ml-8">
                Perfect for tour introductions that users can listen to before arriving at the first physical location.
              </p>
            </div>
            
            {/* GPS Location Section - Only show if NOT intro */}
            {!currentStop.is_intro && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-slate-700">GPS Location *</label>
                  <button
                    type="button"
                    onClick={getCurrentLocation}
                    disabled={gettingLocation}
                    className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center transition-all active:scale-95"
                  >
                    {gettingLocation ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Getting Location...
                      </>
                    ) : (
                      <>
                        <Navigation className="w-4 h-4 mr-2" />
                        Use My Current Location
                      </>
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      name="latitude"
                      value={currentStop.latitude}
                      onChange={handleStopInputChange}
                      data-testid="stop-latitude-input"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                      placeholder="40.7829"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      name="longitude"
                      value={currentStop.longitude}
                      onChange={handleStopInputChange}
                      data-testid="stop-longitude-input"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                      placeholder="-73.9654"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Radius (m)</label>
                    <input
                      type="number"
                      name="radius_meters"
                      value={currentStop.radius_meters}
                      onChange={handleStopInputChange}
                      data-testid="stop-radius-input"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                      placeholder="50"
                    />
                  </div>
                </div>
                
                {/* Smart Radius Suggestions */}
                {(() => {
                  const suggestion = getRadiusSuggestion(
                    parseFloat(currentStop.latitude), 
                    parseFloat(currentStop.longitude)
                  );
                  const overlaps = currentStop.latitude && currentStop.longitude 
                    ? checkRadiusOverlap(
                        parseFloat(currentStop.latitude), 
                        parseFloat(currentStop.longitude), 
                        parseInt(currentStop.radius_meters) || 50
                      ) 
                    : [];
                  
                  return (
                    <>
                      {suggestion.nearestStop && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                          <p className="text-sm text-blue-800">
                            <strong>Nearest stop:</strong> "{suggestion.nearestStop.title}" is <strong>{suggestion.nearestDistance}m</strong> away
                          </p>
                          <p className="text-sm text-blue-600 mt-1">
                            💡 <strong>Suggested max radius: {suggestion.suggestedMax}m</strong>
                            <button
                              type="button"
                              onClick={() => setCurrentStop(prev => ({ ...prev, radius_meters: suggestion.suggestedMax }))}
                              className="ml-2 text-blue-700 underline hover:text-blue-900"
                            >
                              Apply
                            </button>
                          </p>
                        </div>
                      )}
                      
                      {overlaps.length > 0 && (
                        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mt-2">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-semibold text-amber-800">Radius overlap detected!</p>
                              <ul className="text-xs text-amber-600 mt-1">
                                {overlaps.map((o, i) => (
                                  <li key={i}>• "{o.stop.title}" ({o.distance}m away, overlaps by {o.overlapAmount}m)</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
                
                <p className="text-xs text-blue-600 mt-2">
                  📍 Tip: Click "Use My Current Location" while at the actual stop to capture accurate GPS!
                </p>
              </div>
            )}
            
            {/* Navigation Mode */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">How will users get to this stop?</label>
              <select
                value={currentStop.navigation_mode || 'walking'}
                onChange={(e) => setCurrentStop(prev => ({ ...prev, navigation_mode: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                data-testid="stop-navigation-mode"
              >
                <option value="walking">🚶 Walking - Users will walk to this stop</option>
                <option value="driving">🚗 Driving - Users will drive to this stop</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">
                Audio URL (Optional - can add later)
              </label>
              <input
                type="text"
                name="audio_url"
                value={currentStop.audio_url}
                onChange={handleStopInputChange}
                data-testid="stop-audio-url-input"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                placeholder="https://example.com/audio.mp3 or leave blank"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Or Upload Audio File (Optional)</label>
              <label className="flex items-center justify-center w-full px-4 py-8 border-2 border-dashed border-slate-300 rounded-lg hover:border-teal-500 transition-colors cursor-pointer">
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioUpload}
                  data-testid="stop-audio-upload"
                  className="hidden"
                />
                <div className="text-center">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <span className="text-sm text-slate-600 font-body">
                    {uploading ? 'Uploading...' : 'Click to upload audio'}
                  </span>
                </div>
              </label>
            </div>
            <button
              onClick={addStop}
              data-testid="add-stop-button"
              className="w-full bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center justify-center font-sans"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Stop
            </button>
          </div>

          {stops.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-4 font-sans">Added Stops ({stops.length})</h3>
              <p className="text-sm text-slate-500 mb-3">Use arrows to reorder stops</p>
              <div className="space-y-2">
                {stops.map((stop, index) => (
                  <div
                    key={index}
                    data-testid={`stop-item-${index}`}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  >
                    {/* Reorder Buttons */}
                    <div className="flex flex-col mr-3">
                      <button
                        onClick={() => moveStop(index, 'up')}
                        disabled={index === 0}
                        className="p-1 text-slate-500 hover:text-slate-700 disabled:text-slate-300 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveStop(index, 'down')}
                        disabled={index === stops.length - 1}
                        className="p-1 text-slate-500 hover:text-slate-700 disabled:text-slate-300 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center flex-1">
                      <MapPin className="w-5 h-5 text-teal-500 mr-3" />
                      <div>
                        <p className="font-semibold text-slate-900 font-body">
                          {index + 1}. {stop.title}
                        </p>
                        <p className="text-sm text-slate-600 font-body">
                          {stop.latitude && stop.longitude 
                            ? `${parseFloat(stop.latitude).toFixed(4)}, ${parseFloat(stop.longitude).toFixed(4)}`
                            : 'No GPS (Intro)'}
                          {stop.audio_url && <span className="ml-2 text-green-600">• Audio ✓</span>}
                          {!stop.audio_url && <span className="ml-2 text-orange-500">• No audio</span>}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {stop.is_intro && (
                            <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-semibold">
                              🎙️ Intro
                            </span>
                          )}
                          {!stop.is_intro && stop.radius_meters && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">
                              📏 {stop.radius_meters}m
                            </span>
                          )}
                          {stop.navigation_mode === 'driving' && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                              🚗 Drive
                            </span>
                          )}
                          {stop.navigation_mode === 'walking' && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                              🚶 Walk
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeStop(index)}
                      data-testid={`remove-stop-${index}`}
                      className="text-red-500 hover:text-red-600 p-2"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <button
            onClick={() => saveTour(false)}
            disabled={saving}
            data-testid="save-draft-button"
            className="flex-1 bg-slate-500 hover:bg-slate-600 disabled:bg-slate-300 text-white py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:cursor-not-allowed font-sans"
          >
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            onClick={() => saveTour(true)}
            disabled={saving}
            data-testid="publish-tour-button"
            className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 text-white py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:cursor-not-allowed font-sans"
          >
            {saving ? 'Saving...' : 'Save & Publish'}
          </button>
        </div>
        <p className="text-center text-slate-500 text-sm mt-4 font-body">
          Save as draft to add more details later. Publish when ready for customers.
        </p>
      </main>
    </div>
  );
};

export default CreateTourPage;